import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  fetchPersonDetails,
  fetchPersonImages,
  type TmdbPersonCredit,
  type TmdbPersonDetails,
  type TmdbPersonImage,
} from '../tmdb/tmdb'
import { getEffectiveTmdbApiKey, useUserSettings } from '../settings/useUserSettings'
import { LazyImage } from '../ui'
import { useOfflineDetection } from '../hooks/useOfflineDetection'
import { useBackButtonClose } from '../hooks/useBackButtonClose'

/**
 * Department filter tab keys. Mirrors the desktop `PersonDetailView`
 * role grouping (Acting / Directing / Writing / Producing / Other), with
 * an extra "All" tab up front.
 */
type DepartmentTab = 'all' | 'acting' | 'directing' | 'writing' | 'producing' | 'other'

/** Sort keys mirror the desktop view (year / popularity / vote count / title). */
type SortKey = 'year' | 'popularity' | 'votes' | 'title'
type SortDirection = 'asc' | 'desc'

interface Props {
  personId: number | null
  onClose: () => void
  /**
   * Called when the user taps a filmography tile. The library shell wires
   * this to the PlayerModal today (matching the existing PWA PersonModal
   * behaviour); if a future caller wants to open the MovieDetailModal
   * instead, pass `onOpenDetail` and it takes precedence.
   */
  onPlay: (tmdbId: number, type: 'movie' | 'tv') => void
  /**
   * Optional: open the movie/series detail modal instead of playing.
   * When provided, tile clicks are routed here and `onPlay` is ignored.
   * Used by agents wiring detail-in-person-in-detail nav stacks.
   */
  onOpenDetail?: (tmdbId: number, type: 'movie' | 'tv') => void
}

/**
 * Full-screen person detail view — PWA port of the desktop
 * `PersonDetailView`. Matches the desktop feature set within mobile
 * constraints:
 *
 *   1. Hero: profile photo (lazy blur-up), name, known-for department,
 *      birth/death dates + age, birthplace, collapsible biography,
 *      also-known-as chips, IMDb link.
 *   2. Filmography: one tab per department (Acting / Directing /
 *      Writing / Producing / Other) plus "All". Within each tab:
 *      sort by year / popularity / vote count / title with asc/desc
 *      toggle; 24-card pagination with "Load more".
 *   3. Photo gallery: grid of /person/{id}/images profiles with a
 *      lightbox that supports arrow-key navigation.
 *   4. Offline / error recovery: if the initial fetch fails we show a
 *      retry button; if the browser is offline we show an offline
 *      banner and block retries until we come back online.
 *
 * Modal mechanics: Escape closes, backdrop click closes (on md+),
 * focus is trapped inside the sheet and restored to the opener on
 * close. Tapping a filmography tile opens the same nav the parent
 * detail modal uses (play by default; `onOpenDetail` overrides).
 */
export function PersonModal({ personId, onClose, onPlay, onOpenDetail }: Props) {
  const { settings } = useUserSettings()
  // Falls back to the bundled developer key when the user hasn't supplied one.
  const apiKey = getEffectiveTmdbApiKey(settings)
  const language = settings?.tmdbPreferredLanguage || 'en'

  const [details, setDetails] = useState<TmdbPersonDetails | null>(null)
  const [images, setImages] = useState<TmdbPersonImage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isRetrying, setIsRetrying] = useState(false)

  const [bioExpanded, setBioExpanded] = useState(false)
  const [department, setDepartment] = useState<DepartmentTab>('all')
  const [sortKey, setSortKey] = useState<SortKey>('popularity')
  const [sortDir, setSortDir] = useState<SortDirection>('desc')
  const [pageSize, setPageSize] = useState(24)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  // Browser back button closes the photo lightbox before the person
  // modal itself. The parent PersonModal is dismissed by the library
  // shell's own useBackButtonClose on `personId`, so from the user's
  // point of view "back" unwinds: lightbox → person → detail → home.
  useBackButtonClose(lightboxIndex !== null, () => setLightboxIndex(null))

  // Offline detection via the shared PWA hook.
  const { online: isOnline } = useOfflineDetection()

  // Modal refs for focus trap + restore.
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  // Track the current fetch so retries and stale personId changes don't
  // race. Incrementing the token invalidates any in-flight fetch.
  const fetchToken = useRef(0)

  const loadPerson = useCallback(async () => {
    if (personId == null) return
    if (!apiKey) {
      setError('TMDB API key missing — add one in Settings.')
      setLoading(false)
      return
    }

    const token = ++fetchToken.current
    setLoading(true)
    setError(null)

    try {
      const [d, imgs] = await Promise.all([
        fetchPersonDetails(personId, apiKey, language),
        fetchPersonImages(personId, apiKey),
      ])
      if (token !== fetchToken.current) return
      if (!d) {
        setError(
          typeof navigator !== 'undefined' && !navigator.onLine
            ? "You're offline. Person details couldn't be loaded."
            : "Couldn't load person details. Please try again."
        )
        setDetails(null)
        setImages([])
      } else {
        setDetails(d)
        setImages(imgs)
      }
    } catch (err) {
      if (token !== fetchToken.current) return
      console.warn('[PersonModal] fetch failed', err)
      setError(
        typeof navigator !== 'undefined' && !navigator.onLine
          ? "You're offline. Person details couldn't be loaded."
          : "Couldn't load person details. Please try again."
      )
    } finally {
      if (token === fetchToken.current) setLoading(false)
    }
  }, [personId, apiKey, language])

  // Reset per-person UI state and trigger the fetch whenever the id
  // changes. Kept in one effect so a new id always starts from scratch.
  useEffect(() => {
    if (personId == null) {
      setDetails(null)
      setImages([])
      setError(null)
      setLoading(false)
      return
    }
    setBioExpanded(false)
    setDepartment('all')
    setSortKey('popularity')
    setSortDir('desc')
    setPageSize(24)
    setLightboxIndex(null)
    loadPerson()
  }, [personId, loadPerson])

  // Escape closes (lightbox first, then the modal itself).
  useEffect(() => {
    if (personId == null) return
    previouslyFocused.current = (document.activeElement as HTMLElement) || null
    document.body.style.overflow = 'hidden'

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (lightboxIndex !== null) {
          setLightboxIndex(null)
          e.stopPropagation()
          return
        }
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)

    // Move initial focus into the sheet for screen readers + keyboard users.
    const raf = requestAnimationFrame(() => {
      sheetRef.current?.focus()
    })

    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
      cancelAnimationFrame(raf)
      // Restore focus to whatever opened the modal.
      previouslyFocused.current?.focus?.()
    }
  }, [personId, onClose, lightboxIndex])

  // Handle tile click → either open detail (if wired) or play.
  const handleTileClick = useCallback(
    (credit: TmdbPersonCredit) => {
      if (onOpenDetail) {
        onOpenDetail(credit.tmdbId, credit.mediaType)
        return
      }
      onPlay(credit.tmdbId, credit.mediaType)
    },
    [onOpenDetail, onPlay]
  )

  // Department bucketing — map a TMDB crew department to one of our
  // five tabs. Acting is a synthetic department we infer from
  // creditKind === 'cast'.
  const deptCounts = useMemo(() => {
    const counts = {
      all: 0,
      acting: 0,
      directing: 0,
      writing: 0,
      producing: 0,
      other: 0,
    } as Record<DepartmentTab, number>
    if (!details) return counts
    for (const c of details.filmography) {
      counts.all++
      if (c.creditKind === 'cast') {
        counts.acting++
        continue
      }
      const d = (c.department || '').toLowerCase()
      if (d === 'directing') counts.directing++
      else if (d === 'writing') counts.writing++
      else if (d === 'production') counts.producing++
      else counts.other++
    }
    return counts
  }, [details])

  // Filtered + sorted + de-duplicated (by mediaType+tmdbId) filmography
  // for the currently selected tab. Desktop dedupes inside the All-view
  // merge; we do the same so the same title can't appear twice if a
  // person was both actor and writer on the same movie.
  const filteredCredits = useMemo(() => {
    if (!details) return [] as TmdbPersonCredit[]
    const pool = details.filmography.filter((c) => {
      if (department === 'all') return true
      if (department === 'acting') return c.creditKind === 'cast'
      if (c.creditKind !== 'crew') return false
      const d = (c.department || '').toLowerCase()
      if (department === 'directing') return d === 'directing'
      if (department === 'writing') return d === 'writing'
      if (department === 'producing') return d === 'production'
      // 'other' — everything that doesn't land in one of the named buckets.
      return d !== 'directing' && d !== 'writing' && d !== 'production'
    })

    // De-duplicate by (mediaType, tmdbId) — when the same title shows up
    // multiple times (e.g. writer + director) we keep the first match so
    // the user sees one tile per title. Matches desktop "uniqueEntries".
    const seen = new Set<string>()
    const unique: TmdbPersonCredit[] = []
    for (const c of pool) {
      const key = `${c.mediaType}-${c.tmdbId}`
      if (seen.has(key)) continue
      seen.add(key)
      unique.push(c)
    }

    const dir = sortDir === 'asc' ? 1 : -1
    const sorted = [...unique].sort((a, b) => {
      switch (sortKey) {
        case 'year': {
          return ((a.year ?? 0) - (b.year ?? 0)) * dir
        }
        case 'votes': {
          return (a.voteCount - b.voteCount) * dir
        }
        case 'title': {
          return a.title.localeCompare(b.title) * dir
        }
        case 'popularity':
        default: {
          // Desktop's popularity score blends vote count, rating, and
          // TMDB popularity so hit films rise above vanity festival
          // credits. Match it 1:1.
          const score = (c: TmdbPersonCredit) => {
            const votes = c.voteCount
            const rating = c.rating
            const pop = c.popularity
            let s = votes
            if (rating >= 6.5) s *= rating / 5
            s *= Math.log(pop + 1)
            if (votes > 5000) s *= 1.5
            if (votes > 10000) s *= 2
            return s
          }
          return (score(a) - score(b)) * dir
        }
      }
    })
    return sorted
  }, [details, department, sortKey, sortDir])

  const visibleCredits = useMemo(
    () => filteredCredits.slice(0, pageSize),
    [filteredCredits, pageSize]
  )

  // Reset pagination whenever the filter/sort changes so the user
  // doesn't jump to the middle of a smaller list.
  useEffect(() => {
    setPageSize(24)
  }, [department, sortKey, sortDir])

  const hasMore = filteredCredits.length > visibleCredits.length

  const loadMore = useCallback(() => {
    setPageSize((p) => p + 24)
  }, [])

  // Lightbox keyboard nav — arrow keys move through the gallery.
  useEffect(() => {
    if (lightboxIndex === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setLightboxIndex((i) => (i === null ? null : Math.max(0, i - 1)))
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex((i) =>
          i === null ? null : Math.min(images.length - 1, i + 1)
        )
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightboxIndex, images.length])

  // Simple focus trap: when Tab would escape the sheet, wrap around.
  const onSheetKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return
    const root = sheetRef.current
    if (!root) return
    const focusable = root.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const items = Array.from(focusable).filter(
      (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1
    )
    if (items.length === 0) return
    const first = items[0]
    const last = items[items.length - 1]
    const active = document.activeElement as HTMLElement | null
    if (e.shiftKey) {
      if (active === first || !root.contains(active)) {
        last.focus()
        e.preventDefault()
      }
    } else {
      if (active === last) {
        first.focus()
        e.preventDefault()
      }
    }
  }, [])

  if (personId == null) return null

  const age = calculateAge(details?.birthday ?? null, details?.deathday ?? null)

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-bg-primary font-inter"
      role="dialog"
      aria-modal="true"
      aria-label={details?.name || 'Person details'}
    >
      <div
        ref={sheetRef}
        tabIndex={-1}
        onKeyDown={onSheetKeyDown}
        className="flex h-full w-full flex-col focus:outline-none"
      >
        <header className="flex items-center justify-between border-b border-border-primary px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="truncate text-base font-semibold text-text-primary">
              {loading && !details ? 'Loading…' : details?.name || 'Person'}
            </h2>
            <span className="shrink-0 rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-400">
              TMDB
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-bg-secondary px-3 py-1.5 text-sm text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
            aria-label="Close person details"
          >
            Close
          </button>
        </header>

        {!isOnline && (
          <div className="border-b border-yellow-500/30 bg-yellow-500/10 px-4 py-2 text-center text-xs font-semibold text-yellow-400">
            You're offline — showing cached data only.
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading && !details && <HeroSkeleton />}

          {error && !details && (
            <ErrorState
              message={error}
              isOnline={isOnline}
              isRetrying={isRetrying}
              onRetry={async () => {
                setIsRetrying(true)
                try {
                  await loadPerson()
                } finally {
                  setIsRetrying(false)
                }
              }}
            />
          )}

          {details && (
            <div className="mx-auto max-w-5xl p-4 pb-24 md:p-6">
              <HeroSection
                details={details}
                age={age}
                bioExpanded={bioExpanded}
                onToggleBio={() => setBioExpanded((v) => !v)}
              />

              <FilmographySection
                deptCounts={deptCounts}
                department={department}
                onDepartmentChange={setDepartment}
                sortKey={sortKey}
                onSortKeyChange={setSortKey}
                sortDir={sortDir}
                onSortDirChange={setSortDir}
                visibleCredits={visibleCredits}
                totalInView={filteredCredits.length}
                hasMore={hasMore}
                onLoadMore={loadMore}
                onTileClick={handleTileClick}
              />

              {images.length > 0 && (
                <PhotoGallerySection
                  images={images}
                  onOpen={(i) => setLightboxIndex(i)}
                  personName={details.name}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {lightboxIndex !== null && details && images[lightboxIndex] && (
        <Lightbox
          images={images}
          index={lightboxIndex}
          personName={details.name}
          onClose={() => setLightboxIndex(null)}
          onPrev={() =>
            setLightboxIndex((i) => (i === null ? null : Math.max(0, i - 1)))
          }
          onNext={() =>
            setLightboxIndex((i) =>
              i === null ? null : Math.min(images.length - 1, i + 1)
            )
          }
        />
      )}
    </div>
  )
}

// =================== Hero ===================

function HeroSection({
  details,
  age,
  bioExpanded,
  onToggleBio,
}: {
  details: TmdbPersonDetails
  age: string
  bioExpanded: boolean
  onToggleBio: () => void
}) {
  const initials = details.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const showReadMore = !!details.biography && details.biography.length > 300

  return (
    <section className="flex flex-col gap-5 md:flex-row md:gap-6">
      <div className="mx-auto md:mx-0">
        <div className="relative aspect-[2/3] w-48 overflow-hidden rounded-xl border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary shadow-cinematic md:w-60">
          {details.profileUrl ? (
            <LazyImage
              src={details.profileUrl}
              alt={`${details.name} profile photo`}
              className="h-full w-full"
              placeholderColor="#1a1a1a"
              fallback={
                <div className="grid h-full w-full place-items-center">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-white/90">
                      {initials}
                    </div>
                    <div className="mt-1 text-[10px] uppercase tracking-wider text-text-tertiary">
                      No photo
                    </div>
                  </div>
                </div>
              }
            />
          ) : (
            <div className="grid h-full w-full place-items-center">
              <div className="text-center">
                <div className="text-4xl font-bold text-white/90">{initials}</div>
                <div className="mt-1 text-[10px] uppercase tracking-wider text-text-tertiary">
                  No photo
                </div>
              </div>
            </div>
          )}
          {details.imdbId && (
            <a
              href={`https://www.imdb.com/name/${details.imdbId}`}
              target="_blank"
              rel="noreferrer"
              className="absolute bottom-2 right-2 rounded-full bg-yellow-500 px-2 py-1 text-[10px] font-bold text-black shadow-lg transition-colors hover:bg-yellow-400"
              title="View on IMDb"
            >
              IMDb
            </a>
          )}
        </div>
      </div>

      <div className="min-w-0 flex-1 text-center md:text-left">
        <h1 className="text-2xl font-bold text-text-primary md:text-3xl">
          {details.name}
        </h1>
        <div className="mt-1 flex flex-wrap items-center justify-center gap-2 text-sm text-text-secondary md:justify-start">
          {details.knownForDepartment && (
            <span className="font-medium">{details.knownForDepartment}</span>
          )}
          {details.birthday && (
            <>
              <span className="text-text-tertiary">·</span>
              <span>
                Born {formatDate(details.birthday)}
                {age && ` (age ${age})`}
              </span>
            </>
          )}
          {details.deathday && (
            <>
              <span className="text-text-tertiary">·</span>
              <span className="text-red-400">Died {formatDate(details.deathday)}</span>
            </>
          )}
        </div>
        {details.placeOfBirth && (
          <p className="mt-1 text-sm text-text-secondary">
            <span className="text-text-tertiary">Born in:</span>{' '}
            <span className="font-medium">{details.placeOfBirth}</span>
          </p>
        )}

        {details.alsoKnownAs.length > 0 && (
          <div className="mt-3 flex flex-wrap justify-center gap-1.5 md:justify-start">
            {details.alsoKnownAs.slice(0, 6).map((name) => (
              <span
                key={name}
                className="rounded-full border border-border-primary bg-bg-secondary px-2 py-0.5 text-[11px] text-text-tertiary"
              >
                {name}
              </span>
            ))}
          </div>
        )}

        {details.biography ? (
          <div className="mt-4">
            <p
              className={`whitespace-pre-line text-sm leading-relaxed text-text-secondary ${
                bioExpanded || !showReadMore ? '' : 'line-clamp-6'
              }`}
            >
              {details.biography}
            </p>
            {showReadMore && (
              <button
                type="button"
                onClick={onToggleBio}
                className="mt-1 text-[11px] font-semibold text-accent-primary hover:text-accent-hover"
              >
                {bioExpanded ? 'Show less' : 'Read more'}
              </button>
            )}
          </div>
        ) : (
          <p className="mt-4 text-sm text-text-tertiary">No biography available.</p>
        )}
      </div>
    </section>
  )
}

// =================== Filmography ===================

function FilmographySection({
  deptCounts,
  department,
  onDepartmentChange,
  sortKey,
  onSortKeyChange,
  sortDir,
  onSortDirChange,
  visibleCredits,
  totalInView,
  hasMore,
  onLoadMore,
  onTileClick,
}: {
  deptCounts: Record<DepartmentTab, number>
  department: DepartmentTab
  onDepartmentChange: (d: DepartmentTab) => void
  sortKey: SortKey
  onSortKeyChange: (s: SortKey) => void
  sortDir: SortDirection
  onSortDirChange: (d: SortDirection) => void
  visibleCredits: TmdbPersonCredit[]
  totalInView: number
  hasMore: boolean
  onLoadMore: () => void
  onTileClick: (c: TmdbPersonCredit) => void
}) {
  // Only show department tabs that have at least one credit, but
  // always show the All tab.
  const allTabs: Array<{ key: DepartmentTab; label: string; count: number }> = [
    { key: 'all', label: 'All', count: deptCounts.all },
    { key: 'acting', label: 'Acting', count: deptCounts.acting },
    { key: 'directing', label: 'Directing', count: deptCounts.directing },
    { key: 'writing', label: 'Writing', count: deptCounts.writing },
    { key: 'producing', label: 'Producing', count: deptCounts.producing },
    { key: 'other', label: 'Other', count: deptCounts.other },
  ]
  const tabs = allTabs.filter((t) => t.key === 'all' || t.count > 0)

  return (
    <section className="mt-8">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold tracking-tight text-text-primary">
          Filmography
        </h3>
        <div className="flex items-center gap-1.5 text-[11px]">
          <label className="flex items-center gap-1">
            <span className="text-text-tertiary">Sort</span>
            <select
              value={sortKey}
              onChange={(e) => onSortKeyChange(e.target.value as SortKey)}
              className="rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-xs text-text-primary"
            >
              <option value="popularity">Popularity</option>
              <option value="year">Year</option>
              <option value="votes">Vote count</option>
              <option value="title">Title</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => onSortDirChange(sortDir === 'asc' ? 'desc' : 'asc')}
            aria-label={`Sort direction: ${sortDir === 'asc' ? 'ascending' : 'descending'}`}
            className="rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
            title="Toggle sort direction"
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      <div className="-mx-4 mb-4 overflow-x-auto scrollbar-hide md:mx-0">
        <div className="flex w-max gap-1.5 px-4 md:w-auto md:px-0">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => onDepartmentChange(t.key)}
              className={`shrink-0 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                department === t.key
                  ? 'border-accent-primary/40 bg-accent-primary/10 text-accent-primary'
                  : 'border-border-primary bg-bg-secondary text-text-secondary hover:text-text-primary'
              }`}
            >
              {t.label}
              <span className="ml-1.5 rounded bg-black/20 px-1 text-[10px] text-text-tertiary">
                {t.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {visibleCredits.length === 0 ? (
        <div className="py-10 text-center text-sm text-text-tertiary">
          No titles in this category.
        </div>
      ) : (
        <>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {visibleCredits.map((credit) => (
              <li key={credit.creditId}>
                <FilmographyCard credit={credit} onClick={() => onTileClick(credit)} />
              </li>
            ))}
          </ul>
          <div className="mt-5 flex items-center justify-center gap-3">
            <span className="text-[11px] text-text-tertiary">
              Showing {visibleCredits.length} of {totalInView}
            </span>
            {hasMore && (
              <button
                type="button"
                onClick={onLoadMore}
                className="rounded-md bg-accent-primary px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-accent-hover"
              >
                Load more
              </button>
            )}
          </div>
        </>
      )}
    </section>
  )
}

function FilmographyCard({
  credit,
  onClick,
}: {
  credit: TmdbPersonCredit
  onClick: () => void
}) {
  const role = credit.character || credit.job || ''

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full flex-col text-left focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-primary"
      aria-label={`${credit.title}${role ? ` — ${role}` : ''}${
        credit.year ? ` (${credit.year})` : ''
      }`}
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary shadow-cinematic transition-transform duration-300 ease-cinematic group-hover:scale-[1.03]">
        {credit.posterUrl ? (
          <LazyImage
            src={credit.posterUrl}
            alt={credit.title}
            className="h-full w-full"
            placeholderColor="#1a1a1a"
            fallback={
              <div className="grid h-full w-full place-items-center px-2 text-center text-[10px] text-text-tertiary">
                {credit.title}
              </div>
            }
          />
        ) : (
          <div className="grid h-full w-full place-items-center px-2 text-center text-[10px] text-text-tertiary">
            {credit.title}
          </div>
        )}
        {credit.rating > 0 && (
          <div className="absolute left-1 top-1 flex items-center gap-0.5 rounded bg-blue-500/85 px-1.5 py-0.5 text-[9px] font-semibold text-white backdrop-blur-sm">
            ★ {credit.rating.toFixed(1)}
          </div>
        )}
        <div className="absolute left-1 bottom-1 rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
          {credit.mediaType === 'tv' ? 'TV' : 'Movie'}
        </div>
      </div>
      <p className="mt-1.5 line-clamp-2 text-xs font-semibold text-text-primary">
        {credit.title}
      </p>
      <p className="text-[10px] text-text-tertiary">
        {credit.year ?? '—'}
        {role ? ` · ${role}` : ''}
      </p>
    </button>
  )
}

// =================== Photo gallery ===================

function PhotoGallerySection({
  images,
  onOpen,
  personName,
}: {
  images: TmdbPersonImage[]
  onOpen: (index: number) => void
  personName: string
}) {
  return (
    <section className="mt-10">
      <h3 className="mb-3 text-lg font-semibold tracking-tight text-text-primary">
        Photos
        <span className="ml-2 text-xs font-normal text-text-tertiary">
          {images.length}
        </span>
      </h3>
      <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
        {images.slice(0, 24).map((img, i) => (
          <li key={img.filePath}>
            <button
              type="button"
              onClick={() => onOpen(i)}
              className="block w-full overflow-hidden rounded-lg border border-border-primary bg-bg-secondary shadow-cinematic transition-transform duration-300 ease-cinematic hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-accent-primary"
              aria-label={`Open photo ${i + 1} of ${personName}`}
            >
              <div
                className="h-full w-full"
                style={{ aspectRatio: img.aspectRatio || 2 / 3 }}
              >
                <LazyImage
                  src={img.url}
                  alt={`${personName} photo ${i + 1}`}
                  className="h-full w-full"
                  placeholderColor="#1a1a1a"
                />
              </div>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

function Lightbox({
  images,
  index,
  personName,
  onClose,
  onPrev,
  onNext,
}: {
  images: TmdbPersonImage[]
  index: number
  personName: string
  onClose: () => void
  onPrev: () => void
  onNext: () => void
}) {
  const image = images[index]
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${personName} photo ${index + 1} of ${images.length}`}
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onPrev()
        }}
        disabled={index === 0}
        className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white backdrop-blur-sm transition-colors hover:bg-white/20 disabled:opacity-30"
        aria-label="Previous photo"
      >
        ‹
      </button>
      <img
        src={image.url.replace('/w342', '/original')}
        alt={`${personName} photo ${index + 1}`}
        className="max-h-full max-w-full rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onNext()
        }}
        disabled={index >= images.length - 1}
        className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white backdrop-blur-sm transition-colors hover:bg-white/20 disabled:opacity-30"
        aria-label="Next photo"
      >
        ›
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onClose()
        }}
        className="absolute right-4 top-4 rounded-md bg-white/10 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20"
        aria-label="Close photo"
      >
        Close
      </button>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-[11px] text-white/80 backdrop-blur-sm">
        {index + 1} / {images.length}
      </div>
    </div>
  )
}

// =================== Loading / error states ===================

function HeroSkeleton() {
  return (
    <div className="mx-auto max-w-5xl p-4 md:p-6">
      <div className="flex flex-col gap-5 md:flex-row md:gap-6">
        <div className="mx-auto aspect-[2/3] w-48 animate-pulse rounded-xl bg-bg-secondary md:mx-0 md:w-60" />
        <div className="flex-1 space-y-3">
          <div className="h-7 w-1/2 animate-pulse rounded bg-bg-secondary" />
          <div className="h-4 w-1/3 animate-pulse rounded bg-bg-secondary" />
          <div className="mt-4 space-y-2">
            <div className="h-3 w-full animate-pulse rounded bg-bg-secondary" />
            <div className="h-3 w-full animate-pulse rounded bg-bg-secondary" />
            <div className="h-3 w-5/6 animate-pulse rounded bg-bg-secondary" />
            <div className="h-3 w-4/6 animate-pulse rounded bg-bg-secondary" />
          </div>
        </div>
      </div>
    </div>
  )
}

function ErrorState({
  message,
  isOnline,
  isRetrying,
  onRetry,
}: {
  message: string
  isOnline: boolean
  isRetrying: boolean
  onRetry: () => void
}) {
  return (
    <div className="mx-auto max-w-xl p-6">
      <div
        className={`rounded-lg border p-5 text-center ${
          isOnline
            ? 'border-red-500/30 bg-red-500/10'
            : 'border-yellow-500/30 bg-yellow-500/10'
        }`}
      >
        <p
          className={`text-sm font-semibold ${
            isOnline ? 'text-red-300' : 'text-yellow-300'
          }`}
        >
          {message}
        </p>
        <button
          type="button"
          onClick={onRetry}
          disabled={isRetrying || !isOnline}
          className="mt-3 rounded-md bg-accent-primary px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isRetrying ? 'Retrying…' : 'Try again'}
        </button>
      </div>
    </div>
  )
}

// =================== Utilities ===================

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Unknown'
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  } catch {
    return dateString
  }
}

function calculateAge(birthday: string | null, deathday: string | null): string {
  if (!birthday) return ''
  try {
    const birth = new Date(birthday)
    const end = deathday ? new Date(deathday) : new Date()
    let age = end.getFullYear() - birth.getFullYear()
    const monthDiff = end.getMonth() - birth.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && end.getDate() < birth.getDate())) {
      age -= 1
    }
    return age >= 0 ? `${age}` : ''
  } catch {
    return ''
  }
}
